import { Err, isOk, Ok, Result } from '@pacote/result';
import { useStore } from 'react-redux';
import apiRequest from 'services/api/api.config';
import { makeSelectAuthApiConfig } from 'services/api/api.selectors';
import {
  baseResponseSchema,
  RequestApiDataIfNecessaryPayload,
} from 'services/api/api.types';
import { logoutRequest } from 'services/session/session.actions';
import { withCurrentStore } from 'store/currentStore';
import { AppState, ReduxSelector } from 'store/types';
import { useMutation } from 'utils/hooks/query/use-mutation';
import { z } from 'zod/v4';
/* -------------------------------------------------------------------------------------------------
 * types
 * -----------------------------------------------------------------------------------------------*/

const logoutResponseSchema = baseResponseSchema.extend({
  accountCode: z.string(),
  lastLoginTimestamp: z.string(),
  logoutTimestamp: z.string(),
  totalWinAmount: z.number(),
  totalWagerAmount: z.number(),
});

type LogoutIResponse = z.infer<typeof logoutResponseSchema>;

type LogoutServiceResponse = Result<LogoutIResponse, any>;

/* -------------------------------------------------------------------------------------------------
 * config
 * -----------------------------------------------------------------------------------------------*/
const logoutEndpoint = '/authentication-ms/v2/logout';

//TOUCH
export function makeLogoutApiConfig(): ReduxSelector<RequestApiDataIfNecessaryPayload> {
  return function logoutApiConfig(
    reduxState: AppState,
  ): RequestApiDataIfNecessaryPayload {
    return {
      resourceId: logoutEndpoint,
      requestConfig: makeSelectAuthApiConfig({
        method: 'POST',
        url: logoutEndpoint,
        enrichRequestData: false,
      })(reduxState),
      invalidateIfPresent: true,
      skipDefaultServerErrorHandling: true,
    };
  };
}

/* -------------------------------------------------------------------------------------------------
 * fetcher
 * -----------------------------------------------------------------------------------------------*/

const logoutFetcher = async (): Promise<LogoutServiceResponse> => {
  try {
    const apiConfig = makeLogoutApiConfig()(
      withCurrentStore((store) => store.getState()),
    );

    const response = await apiRequest.request<LogoutIResponse>(
      apiConfig.requestConfig,
    );

    return Ok(response.data);
  } catch (fetchError) {
    if (fetchError.response?.data) {
      return Err(fetchError.response?.data);
    }
    return Err('UNKNOWN');
  }
};

/* -------------------------------------------------------------------------------------------------
 * hooks
 * -----------------------------------------------------------------------------------------------*/

const useLogoutMutation = () => {
  const { data, status, mutate } = useMutation(logoutEndpoint, {
    fn: logoutFetcher,
  });
  const reduxStore = useStore();

  const legacyLogout = () => reduxStore.dispatch(logoutRequest());

  const handleLogout = () => {
    // e' momentaneo poi verra aggiunto sulle altre country e incredibile anche su sisal
    if (['pt', 'es'].includes(window.AR_APP_COUNTRY as string)) return mutate();
    legacyLogout();
  };

  const getOkData = () => {
    if (data && isOk(data)) {
      return {
        lastLoginTimestamp: data.value.lastLoginTimestamp,
        logoutTimestamp: data.value.logoutTimestamp,
        totalWagerAmount: data.value.totalWagerAmount,
        totalWinAmount: data.value.totalWinAmount,
      };
    }

    return {
      lastLoginTimestamp: null,
      logoutTimestamp: null,
      totalWagerAmount: 0,
      totalWinAmount: 0,
    };
  };

  return {
    data,
    status,
    ...getOkData(),
    handleLogout,
  };
};

export { useLogoutMutation };
export type { LogoutIResponse };
